import { CountDownService, CountDownTime } from '../../../services/count-down.service';
import { Component, OnInit, Input, EventEmitter, ElementRef, OnDestroy, Output } from '@angular/core';
import { Vehicle, FuelEnum, TransmissionEnum, ReportStatus } from 'src/app/shared/model/vehicle';
import { Observable, Subscription } from 'rxjs';
import { DealerService } from 'src/app/shared/services/dealer.service';
import { OpenService } from 'src/app/shared/services/open.service';
import { UserInfo } from '@app/store/session/session.store';
import { SessionService } from '@app/store/session/session.service';
import { VehicleRemovalReason, VehicleRemovalReasonUtils } from '@app-services/oem-dealer.service';
import { MarketPlacePersonalizationUtils } from '@app-components/layouts/shopkeeper-layout/marketplace-personalization';
import { LiveBidItem, StompService } from '@app-services/stomp.service';
import { delay, retryWhen, take } from 'rxjs/operators';
import { FipeComparisonMode } from '@app-utils/fipe-comparison.utils';

@Component({
  // tslint:disable-next-line: component-selector
  selector: '[app-card-vehicle-cast-off]',
  templateUrl: './card-vehicle-cast-off.component.html',
  styleUrls: ['./card-vehicle-cast-off.component.scss']
})
export class CardVehicleCastOffComponent implements OnInit, OnDestroy {
  @Input() vehicle: Vehicle;
  @Input() ownsVehicle: boolean;
  @Input() doubleUrlDir: boolean;
  contador: CountDownTime;
  imgIndex = 0;
  vehicleImages: string[] = [];
  imgUrls: string[] = [];
  readonly FipeComparisonMode = FipeComparisonMode;
  @Output() clickEmitter = new EventEmitter<void>();

  @Input()
  gridMode?: boolean;

  @Input()
  hideSoldPurchaseInfo?: boolean;

  user: UserInfo;

  readonly STATUS_UPDATE = ["AUCTION_NEW_BID", "AUCTION_REJECTED_BID_SUGGESTION", "AUCTION_SOLD", "FAST_QUOTE_NEW_BID", "FAST_QUOTE_BID_REMOVED"];
  stompSubscription: Subscription;
  constructor(
    private countDownService: CountDownService, private dealerService: DealerService,
    private openService: OpenService,
    private sessionService: SessionService,
    private elRef: ElementRef,
    private stompService: StompService
  ) { }

  ngOnInit() {
    this.openService.getLayoutVars().subscribe(layoutVars => {
      MarketPlacePersonalizationUtils.initCssVars(this.elRef, layoutVars);
    });

    this.sessionService.getUser().subscribe(u => {
      this.user = u;
    });
    this.countDownService.countDownTime(this.vehicle.expirationTimeLeft).subscribe(contador => {
      this.contador = contador;
    });

    if (this.vehicle.imgUrls && this.vehicle.imgUrls.length > 0) {
      this.imgUrls = this.vehicle.imgUrls.filter((imgUrl) => {
        return imgUrl.endsWith('png') || imgUrl.endsWith('jpg') || imgUrl.endsWith('jpeg') || imgUrl.endsWith('webp')
      });
      this.vehicleImages.push(this.imgUrls[0]);
    }

    if (this.ownsVehicle && this.vehicle.hasBids && this.vehicle.active) {
      this.vehicle.currentPrice -= this.vehicle.bidStep;
    }

    if (!this.ownsVehicle && this.vehicle.dealType === 'FAST_QUOTE') {
      this.vehicle.isFastQuote = true;
      if (this.vehicle.id.endsWith('_fastQuote')) {
        this.vehicle.id = this.vehicle.id.substring(0, this.vehicle.id.length - '_fastQuote'.length);
      }else if(this.vehicle.status === 'FAST_QUOTE') {
        this.vehicle.id = this.vehicle.fastQuoteId;
      }
    }
    this.handleStomp();
  }

  ngOnDestroy(): void {
    if (this.stompSubscription) {
      this.stompSubscription.unsubscribe();
      this.stompService.deleteSubscription(this.vehicle?.id);
    }
  }

  get isDanger(): boolean {
    if (this.hideSoldPurchaseInfo) {
      return false;
    }

    return (
      (!this.ownsVehicle &&
        this.vehicle.ownsAnyBid &&
        !this.vehicle.ownsCurrentBid &&
        this.vehicle.active) ||
      (this.vehicle.sold &&
        !this.vehicle.comissionPaid &&
        (this.vehicle.ownsCurrentBid || this.ownsVehicle))
    );
  }

  get isCurrentBid(): boolean {
    return (
      (this.vehicle.ownsCurrentBid || this.vehicle.hasPersonalizedOffer || this.hasPendingCounterOffer) && this.vehicle.active && !this.ownsVehicle
    );
  }

  get hasPendingCounterOffer(): boolean {
    if (this.vehicle?.sold) {
      return false;
    }

    if (!this.vehicle?.ownsAnyBid && !this.vehicle?.hasPersonalizedOffer) {
      return false;
    }

    if (this.vehicle?.hasCounterOfferPending) {
      return true;
    }

    if (this.vehicle?.conditionalOffer?.counterOffer?.status === 'WAITING') {
      return true;
    }
    
    if (Array.isArray(this.vehicle?.alternativeBidRequests)) {
      return this.vehicle.alternativeBidRequests.some(r => r?.status === 'NEW' && r?.counterOffer?.status === 'WAITING');
    }

    return false;
  }

  get counterOfferValueToDisplay(): number | undefined {
    return (
      this.vehicle?.counterOfferValue ??
      this.vehicle?.conditionalOffer?.counterOffer?.value ??
      this.vehicle?.alternativeBidRequests?.find(r => r?.status === 'NEW' && r?.counterOffer?.status === 'WAITING')?.counterOffer?.value
    );
  }

  get originalProposalValueToDisplay(): number | undefined {
    return (
      this.vehicle?.personalizedOfferValue ??
      // Em alguns cenários atuais, a proposta original pode estar como valor de oferta personalizada
      this.vehicle?.currentPrice ??
      this.vehicle?.alternativeBidRequests?.find(r => r?.status === 'NEW' && r?.counterOffer?.status === 'WAITING')?.value
    );
  }

  get valueToDisplay(): number | undefined {
    // Quando existir contra-proposta pendente, o valor principal do box deve ser o valor da contra-proposta.
    return this.hasPendingCounterOffer ? this.counterOfferValueToDisplay : this.vehicle?.currentPrice;
  }


  private handleStomp() {
    if (this.ownsVehicle) {
      return;
    }

    const topic = this.vehicle.isFastQuote ? this.stompService.fastQuoteTopic(this.vehicle.id) : this.stompService.vehicleTopic(this.vehicle.id);

    this.stompSubscription = topic
      .pipe(
        retryWhen(errors => {
          return errors.pipe(
            delay(10000),
            take(10)
          );
        })
      ).subscribe((item: LiveBidItem) => {
        if (this.STATUS_UPDATE.includes(item.event) && item.details) {
          const details = item.details;
          if (!this.vehicle.isFastQuote) {
            this.vehicle.ownsCurrentBid = this.user.dealerId === details.currentBidDealerId;
            this.vehicle.currentPrice = this.vehicle.ownsCurrentBid ? (details.currentPrice - this.vehicle.bidStep) : details.currentPrice;
            this.vehicle.currentBidDealerName = this.vehicle.currentBidDealerName ? details.currentBidDealerName : this.vehicle.currentBidDealerName;
          } else {
            this.vehicle.highestBid = details.currentPrice;
            this.vehicle.bidValue = details.currentBidDealerId === this.user.dealerId ? details.currentPrice : this.vehicle.bidValue;
          }
        }
      });
  }

  emitClick() {
    this.clickEmitter.emit();
  }

  get baseUrlDir(): string {
    if (this.vehicle.isFastQuote && !this.vehicle.sold) {
      return '/cotacao-rapida/veiculos';
    }
    return (this.doubleUrlDir ? '../' : '') + '../veiculos';
  }

  get fuel() {
    return FuelEnum[this.vehicle.fuel];
  }

  get transmission() {
    return TransmissionEnum[this.vehicle.transmission];
  }


  get reportStatus(): string {
    return ReportStatus[this.vehicle.reportStatus];
  }

  toggleFavorite(event: Event): boolean {
    this.vehicle.favorite = !this.vehicle.favorite;
    if (this.vehicle.favorite) {
      this.dealerService.saveAsFavorite(this.vehicle.id, this.vehicle.isFastQuote).subscribe();
    } else {
      this.dealerService.removeAsFavorite(this.vehicle.id, this.vehicle.isFastQuote).subscribe();
    }
    event.stopPropagation();
    return false;
  }

  get slug(): string {
    return this.openService.getVehicleSlug(this.vehicle);
  }

  getSmallImageUrl(imgUrl: string): string {
    if (imgUrl.indexOf('img.autoarremate.com.br') >= 0) {
      const lastDot = imgUrl.lastIndexOf('.');
      return imgUrl.substring(0, lastDot) + '_small' + imgUrl.substring(lastDot);
    } else {
      return imgUrl;
    }
  }

  getDeletionReasonLabel(): string {
    return this.vehicle.status === 'DELETED' ? VehicleRemovalReasonUtils.getExpandedVehicleRemovalReason(this.vehicle.reasonDeletion) : undefined;
  }

  nextImg(vehicle) {
    const imgUrls = vehicle.imgUrls.filter((imgUrl) => {
      return imgUrl.endsWith('png') || imgUrl.endsWith('jpg') || imgUrl.endsWith('jpeg') || imgUrl.endsWith('webp')
    });
    if (this.vehicleImages.length <= 1) {
      this.vehicleImages = this.imgUrls;
    }
    if (this.imgIndex < (imgUrls.length - 1)) {
      this.imgIndex = this.imgIndex + 1;
    }
  }

  prevImg(vehicle) {
    if (this.imgIndex > 0) {
      this.imgIndex = this.imgIndex - 1;
    }
  }
}